import isFunction from 'lodash-es/isFunction';
import utils from '../utils';
import variables from '../variables';

// This method should be called after a user action before openViewer()
// if the latter is called asynchronously, so it prevents popup blockers from
// blocking the opening of the viewer window.
export const prepareViewer = () => () => {
  const windowName = utils.getViewerWindowNameForId(window.presentationId);
  // Try to get a reference to the viewer window,
  // either from the viewer window itself if that's the opener of the current window,
  // or from a stored reference from the opener of the current window.
  if (!window.viewerWindow) {
    if (utils.safeGet(window, 'opener.name') === windowName) {
      window.viewerWindow = window.opener;
    } else if (
      utils.safeGet(window, 'opener.viewerWindow.name') === windowName
    ) {
      window.viewerWindow = window.opener.viewerWindow;
    }
  }

  // In Firefox, window.focus() doesn't focus a tab containing the window,
  // and window.open() doesn't focus the tab either.
  // So we need to first close the tab and reopen a fresh new window to get focus.
  if (window.viewerWindow && !window.viewerWindow.closed && utils.isFirefox) {
    window.viewerWindow.close();
  }

  // If this current window is the opener of the viewer window, chances are we can focus on it.
  // Then we can improve UX by just focusing on it and showing a loading animation instead of
  // closing & reloading it.
  if (
    window.viewerWindow &&
    !window.viewerWindow.closed &&
    utils.safeGet(window, 'viewerWindow.opener.name') === window.name
  ) {
    const api = window.viewerWindow.externalApi;
    if (api && isFunction(api.deinitialize)) {
      // Switch to loading state in viewer just before focusing back on it,
      // so we don't see any previous version for a brief moment at all.
      api.deinitialize();
    }
    // Try to focus
    window.viewerWindow.focus();
  } else {
    // This either opens a new window, or focuses on an existing window
    window.viewerWindow = window.open('about:blank', windowName);
  }

  return window.viewerWindow;
};

// This can be called without prepareViewer(), if within the same execution stack as a user event.
// To be used in an authoring context
export const openViewer = (
  slug,
  hash,
  presentationId,
  domain = window.location.origin,
  draft = false,
) => dispatch => {
  let location;

  if (draft) {
    location = `${domain}/${presentationId}?draft=1`;
  } else {
    location = `${domain}/${slug}`;
  }

  if (hash) {
    location = `${location}#${hash}`;
  }
  // Try to grab an existing reference to an open viewer window.
  // Otherwise, open one and get a new reference.
  let win = window.viewerWindow;
  if (!win || win.closed) {
    win = dispatch(prepareViewer());
  }
  if (win) {
    const isWindowDraft =
      new URLSearchParams(win.location.search).get('draft') === '1';
    const isWindowNew = win.location.href === 'about:blank';
    const isWindowSame = isWindowDraft === draft;
    win.location.replace(location);
    if (!isWindowNew && isWindowSame && !isWindowDraft) {
      // As we only change the hash, we must force reload
      win.location.reload();
    }
    // Try to focus
    win.focus();
  } else {
    // Fallback
    const windowName = utils.getViewerWindowNameForId(presentationId);
    window.open(location, windowName);
  }
  dispatch({ type: 'VIEW_PRESENTATION' });
};

export const openEditor = (id, hash, shouldOpenNewTab = false) => () => {
  let location = `/${id}/edit`;
  if (hash) {
    location = `${location}#${hash}`;
  }
  if (shouldOpenNewTab) {
    const windowName = utils.getEditorWindowNameForId(id);
    // In Firefox, window.focus() doesn't focus a tab containing the window,
    // and window.open() doesn't focus the tab either.
    // So we need to first close the tab and reopen a fresh new window to get focus.
    if (
      window.opener &&
      !window.opener.closed &&
      utils.safeGet(window, 'opener.name') === windowName &&
      utils.isFirefox
    ) {
      window.opener.close();
    }
    window.open(location, windowName);
  } else {
    window.location.href = location;
  }
};

const visitUrl = (url, shouldOpenNewTab = false) => {
  if (shouldOpenNewTab) {
    const win = window.open(url, '_blank');
    win.focus();
  } else {
    window.open(url, '_parent');
  }
};

export const visitViewer = (slug, hash, shouldOpenNewTab = false) => () => {
  let location = `/${slug}`;
  if (hash) {
    location = `${location}#${hash}`;
  }
  visitUrl(location, shouldOpenNewTab);
};

export const visitEditor = (id, hash, shouldOpenNewTab = false) => () => {
  let location = `/${id}/edit`;
  if (hash) {
    location = `${location}#${hash}`;
  }
  visitUrl(location, shouldOpenNewTab);
};

export const visitDashboard = shouldOpenNewTab => () => {
  const location = '/dashboard';
  visitUrl(location, shouldOpenNewTab);
};

export const sharePresentationOnFacebook = presentationUrl => () => {
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      presentationUrl,
    )}`,
    'share',
    'width=640,height=480',
  );
};

export const sharePresentationOnTwitter = (
  presentationTitle,
  presentationUrl,
) => () => {
  window.open(
    `https://twitter.com/intent/tweet/?text=${encodeURIComponent(
      presentationTitle,
    )}&url=${encodeURIComponent(presentationUrl)}&via=ludushq`,
    'share',
    'width=640,height=480',
  );
};

export const sharePresentationOnLinkedin = (
  presentationTitle,
  presentationUrl,
) => () => {
  window.open(
    `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
      presentationUrl,
    )}&title=${encodeURIComponent(presentationTitle)}&source=ludus`,
    'share',
    'width=640,height=480',
  );
};

export const sharePresentationByEmail = (
  presentationTitle,
  presentationUrl,
) => () => {
  window.open(
    `mailto:?subject=${encodeURIComponent(
      presentationTitle,
    )}&body=${encodeURIComponent(presentationUrl)}`,
    '_blank',
  );
};

export const openPresentationPPTX = () => () => {
  window.open(
    `${window.location.origin}/${window.presentationId}/pptx`,
    'pptx',
  );
};

export const openPresentationPDF = () => () => {
  window.open(
    `${window.location.origin}/${window.presentationId}/pdf?quality=0.8&multiplier=2`,
    'pdf',
  );
};

export const openPresentationOffline = () => () => {
  window.open(
    `${window.location.origin}/${window.presentationId}/offline`,
    'offline',
  );
};

export const openPresentationURL = () => () => {
  window.open(`${window.location.origin}/${window.presentationId}/url`, 'url');
};

export const openPresentationClone = () => () => {
  window.open(
    `${window.location.origin}/${window.presentationId}/clone`,
    'clone',
  );
};

export const goToHome = () => () => {
  window.open(variables.urls.home, '');
};

export const openSpeakerView = (slug, hash) => () => {
  let location = `/${slug}/speaker`;
  if (hash) {
    location = `${location}#${hash}`;
  }
  return window.open(
    location,
    variables.windows.speaker,
    'width=1024,height=768',
  );
};
